import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Brain, Check, Play, Repeat, X } from '@phosphor-icons/react';
import { drumLanguageAtom, drumSelectedCognitiveAtom } from '../../../shared/store/drumAppAtoms';
import { useDrumMidi } from '../../../shared/store/MidiContext';
import './CognitiveGame.scss';

export const COGNITIVE_LEVELS = [
  { id: 1, ko: '아주 쉬움', en: 'Very Easy', hintKo: '세 가지 색상으로 천천히 시작', hintEn: 'Start slowly with three colors' },
  { id: 2, ko: '쉬움', en: 'Easy', hintKo: '네 가지 색상의 짧은 순서', hintEn: 'A short four-color sequence' },
  { id: 3, ko: '보통', en: 'Normal', hintKo: '다섯 가지 색상을 골고루 연주', hintEn: 'Play five colors in sequence' },
  { id: 4, ko: '어려움', en: 'Hard', hintKo: '일곱 가지 드럼을 모두 사용', hintEn: 'Use all seven drums' },
  { id: 5, ko: '매우 어려움', en: 'Very Hard', hintKo: '긴 순서를 빠르게 구분', hintEn: 'Follow a longer color sequence' },
];

const DRUMS = [
  { id: 'crash', ko: '크래쉬 심벌', en: 'Crash Cymbal', color: '#ef4455' },
  { id: 'tom1', ko: '탐1', en: 'Tom 1', color: '#ff8520' },
  { id: 'tom2', ko: '탐2', en: 'Tom 2', color: '#f4c615' },
  { id: 'ride', ko: '라이드 심벌', en: 'Ride Cymbal', color: '#36b85d' },
  { id: 'snare', ko: '스네어', en: 'Snare', color: '#2f82ed' },
  { id: 'hihat', ko: '하이햇', en: 'Hi-hat', color: '#ed55a5' },
  { id: 'floor', ko: '플로어', en: 'Floor Tom', color: '#8248d7' },
];

const LEVEL_CONFIG: Record<number, { drums: number; min: number; max: number }> = {
  1: { drums: 3, min: 2, max: 4 },
  2: { drums: 4, min: 2, max: 5 },
  3: { drums: 5, min: 3, max: 6 },
  4: { drums: 7, min: 4, max: 7 },
  5: { drums: 7, min: 5, max: 8 },
};

function getStageLength(level: number, stage: number) {
  const { min, max } = LEVEL_CONFIG[level];
  return Math.min(8, min + Math.floor(((stage - 1) * (max - min)) / 9));
}

function makeSequence(level: number, stage: number) {
  const { drums } = LEVEL_CONFIG[level];
  const length = getStageLength(level, stage);
  return Array.from({ length }, (_, index) => DRUMS[(index * (level + 1) + stage * 3 + Math.floor(index / 2)) % drums].id);
}

function midiStatusLabel(language: string, status: string) {
  if (language === 'ko') {
    if (status === 'connected') return 'MIDI 드럼 연결됨';
    if (status === 'checking') return 'MIDI 권한 확인 중';
    if (status === 'unsupported') return 'MIDI 미지원 브라우저';
    if (status === 'denied') return 'MIDI 권한 필요';
    return 'MIDI 드럼 연결 안 됨';
  }
  if (status === 'connected') return 'MIDI DRUM CONNECTED';
  if (status === 'checking') return 'CHECKING MIDI PERMISSION';
  if (status === 'unsupported') return 'MIDI NOT SUPPORTED';
  if (status === 'denied') return 'MIDI PERMISSION NEEDED';
  return 'MIDI DRUM NOT CONNECTED';
}

function MidiStatus({ language, status, deviceName, onScan }: { language: string; status: string; deviceName: string; onScan: () => void }) {
  const connected = status === 'connected';
  const label = midiStatusLabel(language, status);
  return (
    <button type="button" className={`midi-status-chip ${connected ? 'connected' : 'disconnected'}`} onClick={onScan} title={deviceName || label}>
      <i aria-hidden="true" /><span>{label}</span>
    </button>
  );
}

function CognitiveGame() {
  const language = useAtomValue(drumLanguageAtom);
  const activity = useAtomValue(drumSelectedCognitiveAtom);
  const midi = useDrumMidi();
  const navigate = useNavigate();
  const onExit = () => navigate('/drum/cognitive');
  const activityTitle = language === 'ko'
    ? (activity?.titleKo || '색인지 드럼 활동')
    : (activity?.titleEn || 'Color Drum Activity');
  const activityDescription = language === 'ko'
    ? (activity?.descriptionKo || '화면의 색상 순서를 보고 같은 색의 드럼을 연주합니다.')
    : (activity?.descriptionEn || 'Watch the color sequence and play the matching drum.');

  const [level, setLevel] = useState(1);
  const [started, setStarted] = useState(false);
  const [stage, setStage] = useState(1);
  const [position, setPosition] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [activeDrum, setActiveDrum] = useState<string | null>(null);
  const [mistakeDrum, setMistakeDrum] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const handledMidiAt = useRef<number | null>(null);
  const sequence = useMemo(() => makeSequence(level, stage), [level, stage]);
  const levelData = COGNITIVE_LEVELS.find((item) => item.id === level) || COGNITIVE_LEVELS[0];
  const stageLengths = useMemo(() => Array.from({ length: 10 }, (_, index) => getStageLength(level, index + 1)), [level]);
  const totalProgress = stageLengths.slice(0, stage - 1).reduce((sum, length) => sum + length, 0) + position;
  const totalSteps = stageLengths.reduce((sum, length) => sum + length, 0);

  const hitDrum = (drumId: string, source: 'touch' | 'midi' = 'touch') => {
    if (!started || finished || transitioning || !sequence[position]) return;
    setActiveDrum(drumId);
    window.setTimeout(() => setActiveDrum((current) => (current === drumId ? null : current)), 180);
    if (drumId !== sequence[position]) {
      setCombo(0);
      setScore((value) => Math.max(0, value - 20));
      setMistakeDrum(drumId);
      setFeedback(language === 'ko' ? '색상을 다시 확인해 보세요' : 'CHECK THE COLOR');
      window.setTimeout(() => setMistakeDrum(null), 260);
      return;
    }
    const nextPosition = position + 1;
    setCombo((value) => value + 1);
    setScore((value) => value + 100 + combo * 5);
    setFeedback(source === 'midi' ? (language === 'ko' ? 'MIDI 입력 정확해요!' : 'GREAT MIDI HIT!') : (language === 'ko' ? '좋아요!' : 'GREAT!'));
    if (nextPosition < sequence.length) {
      setPosition(nextPosition);
      return;
    }
    if (stage < 10) {
      setTransitioning(true);
      setPosition(sequence.length);
      window.setTimeout(() => {
        setStage((value) => value + 1);
        setPosition(0);
        setTransitioning(false);
        setFeedback(language === 'ko' ? '다음 스테이지가 시작됩니다' : 'NEXT STAGE');
      }, 420);
      return;
    }
    setPosition(sequence.length);
    window.setTimeout(() => setFinished(true), 450);
  };

  useEffect(() => {
    if (!started || !midi.lastHit || handledMidiAt.current === midi.lastHit.at) return;
    handledMidiAt.current = midi.lastHit.at;
    hitDrum(midi.lastHit.drumId, 'midi');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [midi.lastHit, started, position, stage, finished]);

  const resetGame = () => {
    setStage(1);
    setPosition(0);
    setScore(0);
    setCombo(0);
    setFeedback('');
    setFinished(false);
    setTransitioning(false);
    setStarted(true);
  };

  if (!started) {
    return (
      <main className="cognition-game cognition-level-select" data-localized-root>
        <header className="cognition-game-header">
          <div><Brain weight="duotone" /><span><small>{language === 'ko' ? '인지 활동' : 'COGNITIVE ACTIVITY'}</small><strong>{activityTitle}</strong></span></div>
          <MidiStatus language={language} status={midi.status} deviceName={midi.deviceName} onScan={midi.scan} />
          <button type="button" className="cognition-exit" onClick={onExit}><X weight="bold" />{language === 'ko' ? '종료' : 'EXIT'}</button>
        </header>
        <section className="cognition-level-select-body">
          <div className="cognition-level-intro">
            <span>{language === 'ko' ? '색상과 드럼 연주' : 'COLOR & DRUM PLAY'}</span>
            <h1>{language === 'ko' ? '레벨을 선택하세요' : 'Choose a level'}</h1>
            <p>{activityDescription}</p>
          </div>
          <div className="cognition-launch-levels">
            {COGNITIVE_LEVELS.map((item) => (
              <button type="button" className={level === item.id ? 'active' : ''} aria-pressed={level === item.id} key={item.id} onClick={() => setLevel(item.id)}>
                <b>{item.id}</b>
                <span><strong>{language === 'ko' ? item.ko : item.en}</strong><small>{language === 'ko' ? item.hintKo : item.hintEn}</small></span>
                {level === item.id && <Check weight="bold" />}
              </button>
            ))}
          </div>
          <button type="button" className="cognition-launch-start" onClick={resetGame}><Play weight="fill" />{language === 'ko' ? '활동 시작' : 'START ACTIVITY'}</button>
        </section>
      </main>
    );
  }

  if (finished) {
    return (
      <main className="cognition-game cognition-complete color-drum-complete" data-localized-root>
        <button type="button" className="cognition-exit" onClick={onExit}><X weight="bold" />{language === 'ko' ? '종료' : 'EXIT'}</button>
        <div>
          <span><Check weight="bold" /></span>
          <small>{language === 'ko' ? '활동 완료' : 'ACTIVITY COMPLETE'}</small>
          <h1>{language === 'ko' ? '색상 순서를 모두 연주했어요!' : 'Color sequence complete!'}</h1>
          <p>{language === 'ko' ? `${levelData.ko} · 최종 점수 ${score.toLocaleString()}점` : `${levelData.en} · Final score ${score.toLocaleString()}`}</p>
          <button type="button" onClick={resetGame}><Repeat weight="bold" />{language === 'ko' ? '다시 시작' : 'PLAY AGAIN'}</button>
          <button type="button" className="secondary" onClick={onExit}>{language === 'ko' ? '인지 활동 목록' : 'ACTIVITY LIST'}</button>
        </div>
      </main>
    );
  }

  return (
    <main className="color-drum-game" data-localized-root>
      <header className="color-drum-game-header">
        <div className="color-drum-title"><Brain weight="duotone" /><span><small>{activityTitle}</small><strong>{language === 'ko' ? levelData.ko : levelData.en}</strong></span></div>
        <div className="color-drum-stats"><span>{language === 'ko' ? '점수' : 'SCORE'}<strong>{score.toLocaleString()}</strong></span><span>{language === 'ko' ? '콤보' : 'COMBO'}<strong>{combo}</strong></span></div>
        <MidiStatus language={language} status={midi.status} deviceName={midi.deviceName} onScan={midi.scan} />
        <button type="button" className="cognition-exit" onClick={onExit}><X weight="bold" />{language === 'ko' ? '종료' : 'EXIT'}</button>
      </header>
      <section className="color-drum-stage">
        <div className="color-drum-instruction">
          <div><span>{language === 'ko' ? `${stage}/10 스테이지 · ${sequence.length}개` : `STAGE ${stage}/10 · ${sequence.length} HITS`}</span><h1>{language === 'ko' ? '색상 순서대로 드럼을 연주하세요' : 'Play the drums in color order'}</h1></div>
          <p>{language === 'ko' ? 'MIDI 드럼 또는 화면의 드럼을 터치할 수 있습니다.' : 'Use your MIDI drum or touch the drums on screen.'}</p>
        </div>
        <div className="color-sequence" aria-label={language === 'ko' ? '연주할 색상 순서' : 'Color sequence to play'}>
          {sequence.map((drumId, index) => {
            const drum = DRUMS.find((item) => item.id === drumId)!;
            return (
              <i key={`${stage}-${index}`} className={index < position ? 'complete' : index === position ? 'current' : ''} style={{ '--drum-color': drum.color } as React.CSSProperties}>
                <span>{index < position && <Check weight="bold" />}</span>
              </i>
            );
          })}
        </div>
        <div className="color-drum-feedback" aria-live="polite">{feedback || (language === 'ko' ? '첫 번째 색상을 확인하세요' : 'WATCH THE FIRST COLOR')}</div>
        <div className="drum-pad-grid">
          {DRUMS.map((drum) => (
            <button type="button" data-drum-id={drum.id} className={`cognitive-drum-pad ${activeDrum === drum.id ? 'is-hit' : ''} ${mistakeDrum === drum.id ? 'is-wrong' : ''}`} style={{ '--drum-color': drum.color } as React.CSSProperties} key={drum.id} onPointerDown={() => hitDrum(drum.id, 'touch')} aria-label={language === 'ko' ? drum.ko : drum.en}>
              <span className="drum-pad-face"><i /></span>
              <strong>{language === 'ko' ? drum.ko : drum.en}</strong>
            </button>
          ))}
        </div>
      </section>
      <footer className="color-drum-progress"><span><i style={{ width: `${Math.min(100, (totalProgress / totalSteps) * 100)}%` }} /></span><strong>{Math.min(totalProgress, totalSteps)} / {totalSteps}</strong></footer>
    </main>
  );
}

export default CognitiveGame;
